import { cur } from '@/lib/currency';
import { useEffect, useState, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Inbox, ArrowRight, RefreshCw, CheckCircle2, Truck, Users, UserPlus, Briefcase, Sparkles, Trash2, PlayCircle, ArrowDownCircle, ArrowUpCircle, Receipt, Landmark, Gift, HandCoins, FileQuestion, Copy, Link2, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import BankingFlowRecap from '@/components/common/BankingFlowRecap';
import { PageTour } from '@/components/common/PageTour';
import GettingStartedChecklist from '@/components/common/GettingStartedChecklist';
import { QuickLinkPickerDialog } from './banking/QuickLinkPickerDialog';
import { PlatformIbanBanner } from '@/components/banking/PlatformIbanBanner';
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore';

/**
 * Banking Hub: Quick-link mode.
 *
 * Inbox-style screen that surfaces every un-linked outgoing bank
 * payment in one column, lets the user pick the matching entity
 * type (Supplier / Customer / Employee / Partner / Expense), and
 * then pick the specific entity from a searchable list. Backend
 * generates the right downstream record in a single click and the
 * row drops out of the queue.
 *
 * Goal: process the inbox to zero — the same UX shape as Gmail's
 * archive workflow. Mirrors the language we use everywhere else
 * (Convert to expense / + Expense) but applied to all 5 downstream
 * collections, not just expenses.
 */
const ENTITY_TYPES = [
  { key: 'supplier', label: 'Supplier', icon: Truck, tone: 'amber', endpoint: '/suppliers' },
  { key: 'customer', label: 'Customer', icon: Users, tone: 'sky', endpoint: '/customers' },
  { key: 'employee', label: 'Employee', icon: UserPlus, tone: 'emerald', endpoint: '/employees' },
  { key: 'partner',  label: 'Partner',  icon: Briefcase, tone: 'violet', endpoint: '/partners' },
  // iter386 — expand the 4-chip outgoing picker with expense_category + government.
  { key: 'expense_category', label: 'Expense Cat.', icon: Receipt, tone: 'stone', endpoint: '/categories' },
  { key: 'government', label: 'Government', icon: Landmark, tone: 'rose', endpoint: null /* tag-only */ },
];

// iter386 — incoming rows map to 6 "purpose" chips. Each chip resolves to a
// concrete downstream entity/category when the user picks one. Customer & employee
// pickers reuse existing list endpoints; the refund/other-income/gov-refund
// paths create an income row tagged with the chosen expense-category, letting
// admins keep cash-flow clean without polluting the supplier ledger.
const INCOMING_PURPOSES = [
  { key: 'customer_payment', label: 'Customer Payment', icon: Users, tone: 'sky', mapTo: 'customer', endpoint: '/customers', help: 'Invoice/outstanding settled' },
  { key: 'advance_received', label: 'Advance Received', icon: HandCoins, tone: 'emerald', mapTo: 'customer', endpoint: '/customers', help: 'Customer paid before invoice' },
  // iter395q — Online-platform settlement (HungerStation, Jahez, Talabat,
  // ToYou, Keta, Ninja). User reported these deposits weren't auto-linking
  // from bank statements; root cause was platform missing from the
  // matcher's ENTITY_META — fixed in beneficiaries.py. This chip lets
  // users manually confirm the platform when the matcher misses too.
  { key: 'platform_settlement', label: 'Platform Settlement', icon: Banknote, tone: 'cyan', mapTo: 'platform', endpoint: '/platforms', help: 'HungerStation / Jahez / Talabat payout' },
  { key: 'refund', label: 'Refund',  icon: ArrowDownCircle, tone: 'amber', mapTo: 'expense_category', endpoint: '/categories', help: 'Money back from supplier/employee' },
  { key: 'other_income', label: 'Other Income',  icon: Gift, tone: 'violet', mapTo: 'expense_category', endpoint: '/categories', help: 'Rental, interest, misc income' },
  { key: 'government_refund', label: 'Government Refund', icon: Landmark, tone: 'rose', mapTo: 'expense_category', endpoint: '/categories', help: 'VAT/Zakat/Tax refund' },
  { key: 'custom', label: 'Custom / Other',  icon: FileQuestion, tone: 'stone', mapTo: 'expense_category', endpoint: '/categories', help: 'Anything else' },
];

// iter391 — Per-entity subtype options. Drives the "salary / advance / EID
// bonus / penalty" dropdown shown above the searchable picker so the user
// can disambiguate within a single entity type. Backend already accepts a
// `subtype` field on link-to-entity (added in iter386); this map is the
// frontend's source of truth for what each chip exposes.
//
// Key shape: { value, label, help? }. Default (always first) is the most
// common case for that entity. Additional cases the backend currently
// stores under a free-text `type` field on the downstream doc.
const SUBTYPES = {
  // Outgoing employee → 5 cases per the user's iter391 spec
  employee: [
    { value: 'salary', label: 'Salary', help: 'Monthly pay or part-payment' },
    { value: 'advance', label: 'Advance', help: 'Salary advance / loan' },
    { value: 'eid_bonus', label: 'EID / Bonus', help: 'Festival or performance bonus' },
    { value: 'penalty', label: 'Penalty / Deduction', help: 'Fine deducted (negative)' },
    { value: 'reimbursement', label: 'Reimbursement', help: 'Out-of-pocket expense paid back' },
  ],
  // Outgoing partner — draw / capital injection / loan return / salary
  partner: [
    { value: 'draw', label: 'Owner Draw', help: 'Personal withdrawal from profit' },
    { value: 'capital_return', label: 'Capital Return', help: 'Returning their original investment' },
    { value: 'loan_repayment', label: 'Loan Repayment', help: 'Paying back a partner loan' },
    { value: 'salary', label: 'Partner Salary', help: 'Periodic compensation' },
    { value: 'expense_reimbursement', label: 'Expense Reimburse', help: 'Reimbursing partner expenses' },
  ],
  // Outgoing expense_category — government fees, fines, asset purchase, etc
  expense_category: [
    { value: 'general', label: 'General Expense' },
    { value: 'fine', label: 'Fine / Penalty', help: 'Late fees, traffic, regulatory' },
    { value: 'asset_purchase', label: 'Asset Purchase', help: 'Equipment, furniture, vehicles' },
    { value: 'utility', label: 'Utility Bill', help: 'Power, water, internet' },
    { value: 'rent', label: 'Rent', help: 'Premises rental' },
    { value: 'insurance', label: 'Insurance', help: 'Premiums, renewals' },
    { value: 'fuel', label: 'Fuel / Vehicle', help: 'Petrol, maintenance' },
  ],
  // Outgoing supplier — type of bill being paid
  supplier: [
    { value: 'bill_payment', label: 'Bill Payment', help: 'Settling an existing bill' },
    { value: 'advance_payment', label: 'Advance Payment', help: 'Paid before bill received' },
    { value: 'deposit', label: 'Security Deposit' },
  ],
  // Outgoing customer (refunds)
  customer: [
    { value: 'refund', label: 'Refund', help: 'Returning money to customer' },
    { value: 'rebate', label: 'Rebate / Cashback' },
  ],
  // Government — type of govt payment
  government: [
    { value: 'vat', label: 'VAT' },
    { value: 'zakat', label: 'Zakat / Tax' },
    { value: 'municipality', label: 'Municipality Fee' },
    { value: 'gosi', label: 'GOSI' },
    { value: 'other_gov', label: 'Other Government' },
  ],
};

export default function BankingQuickLinkPage() {
  // iter593 — Hide Partner chip on the link-target picker unless tenant opted in.
  const partnersEnabled = useFeatureFlagsStore(s => s.flags.enable_partner_entries);
  // iter412 — Optional `?stale=1` query param wires the SmartReconcile
  // widget's "Needs review" stat directly into a filtered Quick-Link
  // inbox: only txns whose date is older than 7 days are shown.
  const [searchParams, setSearchParams] = useSearchParams();
  const staleOnly = searchParams.get('stale') === '1';
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);  // currently-focused row in the inbox
  const [pickerType, setPickerType] = useState(null); // when set, opens the entity picker
  const [pickerOptions, setPickerOptions] = useState([]);
  const [suggestedTop, setSuggestedTop] = useState([]); // iter386b — smart suggestions from /bank-matching/lookup
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [seeding, setSeeding] = useState(false);
  // iter409 — Re-match progress flag for the "Re-match all statements" button.
  const [rematching, setRematching] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [minConfidence, setMinConfidence] = useState(0); // 0|70|85|95
  // iter386 — direction toggle. The queue backend supports 'outgoing' and
  // 'incoming'. Switching rewinds the selection + re-fetches so the UX
  // matches either money-out or money-in flows.
  const [direction, setDirection] = useState('outgoing');
  // iter395 — Show already-linked rows alongside un-linked ones so the
  // user can audit / re-link a row that was matched in error. Backed by
  // a server query param so the queue is correctly filtered server-side.
  const [includeLinked, setIncludeLinked] = useState(false);
  // Date range for the queue — lets users navigate older bank-statement
  // imports that would otherwise be crowded out by the newest 200 rows.
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // iter391 — Branch override + subtype on link.
  // Bank txns sometimes pay for another branch (e.g., HQ pays a fine
  // for Branch B). The `pickerBranchId` state lets the user choose the
  // PAYING branch when linking; defaults to the bank-payment's own
  // branch_id but can be overridden. `pickerSubtype` carries the chip
  // subtype dropdown value (salary / advance / fine / asset / etc).
  const [branches, setBranches] = useState([]);
  const [pickerBranchId, setPickerBranchId] = useState('');
  const [pickerSubtype, setPickerSubtype] = useState('');
  // iter395 — Optional override description the user can type before
  // creating the downstream record (expense/payment/etc). Pre-filled
  // with the bank line's own description so users only edit when the
  // bank line is unhelpful (e.g., a generic "TRF" code).
  const [pickerDescription, setPickerDescription] = useState('');

  // iter391 — In-session "Recently linked" panel so the user can
  // unlink a row they just processed without leaving Quick-Link.
  // The queue itself only returns un-linked rows so once a row is
  // processed it disappears — this state remembers them locally.
  const [recentLinks, setRecentLinks] = useState([]); // {row, link, createdAt}

  // iter387 — Auto-Rule suggestion memory. When a user links 2 similar
  // rows in a session (same purpose + same counterparty keyword), we
  // fire a toast offering to create a banking rule so future rows
  // auto-match. Session-scoped (Map cleared on page unload) — the
  // server-side rule suggestion endpoint is the long-term memory.
  const similarLinksRef = useRef(new Map()); // key → {count, lastPayload}
  const toastedKeysRef = useRef(new Set());  // keys we've already offered a rule for

  // iter388 — duplicate-candidate detection. When the user selects a
  // row in the inbox we call the backend to see whether the same
  // economic event was already entered manually (as an expense, bill,
  // advance, partner draw, etc.). If yes, we show a yellow panel above
  // the chip grid offering "Link to existing" — one click attaches the
  // bank row to the existing doc instead of creating a duplicate.
  const [dupCandidates, setDupCandidates] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupDismissed, setDupDismissed] = useState(new Set()); // rowIds where user dismissed the panel
  const dupCacheRef = useRef(new Map());

  // Does the current queue contain any demo rows? Used to decide whether
  // to show the "Clear demo data" button vs the "Seed demo queue" one.
  const hasDemoRows = queue.some((r) => r.is_demo);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/banking/quick-link/seed-demo');
      if (data.inserted > 0) {
        toast.success(`Seeded ${data.inserted} demo row${data.inserted === 1 ? '' : 's'} into the queue`);
      } else {
        toast.info(`Demo rows already present (${data.total_demo_rows})`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not seed demo queue');
    } finally {
      setSeeding(false);
    }
  };

  const clearDemo = async () => {
    if (!window.confirm('Remove all unlinked demo rows from the queue?')) return;
    setSeeding(true);
    try {
      const { data } = await api.delete('/banking/quick-link/seed-demo');
      toast.success(`Removed ${data.removed} demo row${data.removed === 1 ? '' : 's'}`);
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not clear demo rows');
    } finally {
      setSeeding(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const qsObj = {
        direction,
        limit: '200',
        min_confidence: String(minConfidence),
      };
      if (includeLinked) qsObj.include_linked = 'true';
      if (dateStart) qsObj.start_date = dateStart;
      if (dateEnd) qsObj.end_date = dateEnd;
      const qs = new URLSearchParams(qsObj).toString();
      const r = await api.get(`/banking/quick-link/queue?${qs}`);
      setQueue(r.data.rows || []);
      // Auto-select the first row so the right-pane action panel is
      // populated immediately on page load — saves a click.
      setSelected((r.data.rows || [])[0] || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [minConfidence, direction, dateStart, dateEnd, includeLinked]);

  // iter391 — Load branches once for the picker's "Paying branch" dropdown.
  useEffect(() => {
    let alive = true;
    api.get('/branches').then((r) => {
      if (!alive) return;
      const list = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      setBranches(list.filter((b) => b?.id).map((b) => ({ id: b.id, name: b.name || b.code || '—' })));
    }).catch(() => { /* no-op — branch dropdown gracefully degrades to "default" */ });
    return () => { alive = false; };
  }, []);

  // iter388 — when the selected row changes, ask the backend for any
  // existing documents that look like a duplicate. The response is
  // cached per row id to keep re-selection instant.
  useEffect(() => {
    if (!selected) { setDupCandidates([]); return; }
    const rowKey = selected.id || `${selected.statement_id}:${selected.txn_index}`;
    // If the user already dismissed the panel for this row, don't re-show.
    if (dupDismissed.has(rowKey)) { setDupCandidates([]); return; }
    if (dupCacheRef.current.has(rowKey)) {
      setDupCandidates(dupCacheRef.current.get(rowKey));
      return;
    }
    const run = async () => {
      setDupLoading(true);
      try {
        const params = selected.row_type === 'statement_txn'
          ? { row_id: rowKey, row_type: 'statement_txn', statement_id: selected.statement_id, txn_index: selected.txn_index }
          : { row_id: selected.id, row_type: 'bank_payment' };
        const r = await api.get('/banking/quick-link/duplicate-candidates', { params });
        const list = r.data?.candidates || [];
        dupCacheRef.current.set(rowKey, list);
        setDupCandidates(list);
      } catch (e) {
        // Silent — duplicate check is a bonus signal, not required.
        setDupCandidates([]);
      } finally {
        setDupLoading(false);
      }
    };
    run();
  }, [selected, dupDismissed]);

  const submitLinkToExisting = async (candidate) => {
    if (!selected || !candidate) return;
    setSubmitting(true);
    try {
      const body = selected.row_type === 'statement_txn'
        ? {
            row_type: 'statement_txn',
            statement_id: selected.statement_id,
            txn_index: selected.txn_index,
            existing_collection: candidate.collection,
            existing_id: candidate.id,
          }
        : {
            row_type: 'bank_payment',
            row_id: selected.id,
            existing_collection: candidate.collection,
            existing_id: candidate.id,
          };
      await api.post('/banking/quick-link/link-to-existing', body);
      toast.success(`Linked to existing ${candidate.collection.replace('_', ' ')} — no duplicate created`);
      setProcessedCount((n) => n + 1);
      const remaining = queue.filter((r) => r.id !== selected.id);
      setQueue(remaining);
      setSelected(remaining[0] || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link failed');
    } finally {
      setSubmitting(false);
    }
  };

  const dismissDupPanel = () => {
    if (!selected) return;
    const rowKey = selected.id || `${selected.statement_id}:${selected.txn_index}`;
    setDupDismissed((prev) => new Set([...prev, rowKey]));
    setDupCandidates([]);
  };

  // Open the entity picker for the chosen type. We fetch the relevant
  // entity list lazily on click (small payload) so the page itself
  // loads fast even on tenants with thousands of suppliers/customers.
  //
  // iter386 — `opt` can be either an ENTITY_TYPES entry (outgoing flow)
  // or an INCOMING_PURPOSES entry (incoming flow). For tag-only targets
  // (government / no entity endpoint) we skip the picker and link
  // directly with a synthetic entity.
  const openPicker = async (opt) => {
    if (!selected) { toast.error('Pick a row from the inbox first'); return; }
    // iter391 — pre-seed the branch + subtype dropdowns so the picker
    // dialog renders with sensible defaults the user can override.
    setPickerBranchId(selected.paying_branch_id || selected.branch_id || '');
    // Default the description to the bank line's own narrative so the
    // user can leave it untouched in 90% of cases.
    setPickerDescription(selected.description || selected.payee_label || selected.party_name || '');
    const stKey = opt.mapTo || opt.key;
    const stList = SUBTYPES[stKey];
    setPickerSubtype((stList && stList[0]?.value) || '');
    // iter395p — Tag-only targets (Government, Government Refund, Other
    // Income, Refund) used to skip the picker entirely and link with a
    // synthetic {id:null}. Users complained: "when I select Gov it's
    // not showing me payment options". They want the picker to open so
    // they can pick the SUBTYPE (Zakat / VAT / Income Tax / Other) and
    // optionally edit the branch + description before linking.
    // Now we open the picker like normal — the entity list is just empty
    // for tag-only flows; the user selects the subtype and clicks Save.
    if (!opt.endpoint) {
      setPickerType(opt);
      setPickerSearch('');
      setPickerLoading(false);
      setPickerOptions([]);
      setSuggestedTop([]);
      return;
    }
    setPickerType(opt);
    setPickerSearch('');
    setPickerLoading(true);
    setPickerOptions([]);
    setSuggestedTop([]);
    try {
      const r = await api.get(opt.endpoint);
      const list = Array.isArray(r.data) ? r.data : (r.data?.data || r.data?.[`${opt.key}s`] || []);
      const all = list.filter((x) => x?.id).map((x) => ({
        id: x.id,
        name: x.name || x.full_name || '—',
        sub: x.phone || x.email || x.tax_number || '',
      }));
      setPickerOptions(all);

      // iter386b — Ask the smart-match index for entities that already have
      // bank credentials matching this row (IBAN/account/name). Surface the
      // top hits as a "Suggested" section. Filter to the requested entity_type.
      try {
        const wantType = opt.mapTo || opt.key;
        const ibanCandidate = selected.counterparty_iban || selected.iban || selected.payer_iban || selected.payee_iban;
        const acctCandidate = selected.counterparty_account || selected.account_number || selected.payer_account || selected.payee_account;
        const nameCandidate = selected.payee_name || selected.payer_name || selected.party_name || selected.description || '';
        if (ibanCandidate || acctCandidate || nameCandidate) {
          const sm = await api.post('/bank-matching/lookup', {
            iban: ibanCandidate,
            account_number: acctCandidate,
            name: nameCandidate,
            description: selected.description || selected.narrative || '',
            top_k: 5,
          });
          const cands = (sm.data?.candidates || []).filter(c =>
            c?.ref?.entity_type === wantType && c?.ref?.entity_id,
          );
          // Map to picker shape with confidence chip
          const ids = new Set();
          const top = [];
          for (const c of cands) {
            const id = c.ref.entity_id;
            if (ids.has(id)) continue;
            ids.add(id);
            const matched = all.find(o => o.id === id);
            top.push({
              id,
              name: matched?.name || c.ref?.entity_name || '—',
              sub: matched?.sub || (c.matched_field ? `via ${c.matched_field}` : ''),
              confidence: c.confidence,
              hint: c.matched_field,
            });
          }
          setSuggestedTop(top);
        }
      } catch { /* lookup is best-effort; ignore failures */ }
    } catch (e) {
      toast.error(`Could not load ${opt.label.toLowerCase()}`);
    } finally {
      setPickerLoading(false);
    }
  };

  const submitLink = async (entity, typeOverride) => {
    const pt = typeOverride || pickerType;
    if (!selected || !pt) return;
    setSubmitting(true);
    try {
      const entity_type = pt.mapTo || pt.key;
      // iter391 — when the picker dropdown was used, prefer its choice;
      // fall back to the chip key (e.g., advance_received) otherwise.
      const subtype = pickerSubtype || (pt.mapTo ? pt.key : undefined);
      const branch_id = pickerBranchId || undefined;
      // iter395 — picker-provided description overrides the auto-generated
      // one. Trimmed so empty strings fall through to the default.
      const customDescription = (pickerDescription || '').trim();
      let resp;
      if (selected.row_type === 'statement_txn') {
        resp = await api.post('/banking/statement-txn/link-to-entity', {
          statement_id: selected.statement_id,
          txn_index: selected.txn_index,
          entity_type,
          entity_id: entity.id,
          subtype,
          branch_id,
          description: customDescription || undefined,
        });
      } else {
        resp = await api.post(`/banking/payments/${selected.id}/link-to-entity`, {
          entity_type,
          entity_id: entity.id,
          subtype,
          branch_id,
          description: customDescription || entity.name || pt.label,
        });
      }
      toast.success(`Linked as ${pt.label}${entity.name ? ` — ${entity.name}` : ''}`);
      setProcessedCount((n) => n + 1);
      maybeOfferRule({ selectedRow: selected, purpose: pt, entity });
      // iter391 — capture this link in the recent-links panel so the
      // user can undo it without leaving the page.
      const created = resp?.data || {};
      // iter404 — `_link_statement_txn_core` only stamps the txn (no
      // downstream doc), so its response carries no collection/doc.id
      // — only `beneficiary_id`. Use a sentinel collection so the Undo
      // dialog can still render and the request shape stays uniform.
      const isStmtTxn = selected.row_type === 'statement_txn';
      setRecentLinks((prev) => [{
        id: `${Date.now()}-${Math.random()}`,
        row: { ...selected },  // snapshot so it survives queue mutation
        link: {
          collection: isStmtTxn ? '__stmt_txn__' : (created.collection || ''),
          id: isStmtTxn ? (created.beneficiary_id || '__stmt_txn__') : (created.doc?.id || created.existing_id || ''),
          beneficiary_id: created.beneficiary_id || '',
          name: entity.name || pt.label,
          subtype,
          branch_id,
          purpose_label: pt.label,
        },
        createdAt: Date.now(),
      }, ...prev].slice(0, 10));
      // Drop the row from the queue and pick the next one
      const remaining = queue.filter((r) => r.id !== selected.id);
      setQueue(remaining);
      setSelected(remaining[0] || null);
      setPickerType(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link failed');
    } finally {
      setSubmitting(false);
    }
  };

  // iter391 — Undo a link from the recent-links panel. Calls the same
  // unlink endpoint added in iter390 (Banking Hub) — `delete_downstream`
  // is true because Quick-Link only ever creates new docs.
  //
  // iter404 — Two row shapes need different validation:
  //   • bank_payment rows  → require link.id + link.collection (real
  //     downstream doc) — those are the originals from iter390.
  //   • statement_txn rows → no downstream doc was created (we only
  //     stamped the txn + upserted a beneficiary). The sentinel
  //     '__stmt_txn__' collection means "just unstamp the txn".
  const undoRecentLink = async (entry, deleteDownstream) => {
    const isStmtTxn = entry?.row?.row_type === 'statement_txn';
    if (!entry?.link) {
      toast.error('Cannot undo — link details missing');
      return;
    }
    if (!isStmtTxn && (!entry.link.id || !entry.link.collection)) {
      toast.error('Cannot undo — link details missing');
      return;
    }
    if (isStmtTxn && (!entry.row.statement_id || entry.row.txn_index === undefined)) {
      toast.error('Cannot undo — txn coordinates missing');
      return;
    }
    const human = isStmtTxn
      ? `the bank statement line for ${entry.link.name}`
      : `the ${entry.link.collection.replace('_', ' ')} record for ${entry.link.name}`;
    if (!window.confirm(deleteDownstream
      ? (isStmtTxn
          ? `Detach ${human} and remove the auto-created beneficiary? The transaction will return to the inbox.`
          : `Delete ${human}? This cannot be undone.`)
      : `Detach ${human}? The record stays in your books, only the bank link is removed.`
    )) return;
    try {
      const body = isStmtTxn
        ? {
            row_type: 'statement_txn',
            statement_id: entry.row.statement_id,
            txn_index: entry.row.txn_index,
            beneficiary_id: entry.link.beneficiary_id || undefined,
            delete_downstream: deleteDownstream,
          }
        : {
            row_type: 'bank_payment',
            row_id: entry.row.id,
            downstream_collection: entry.link.collection,
            downstream_id: entry.link.id,
            delete_downstream: deleteDownstream,
          };
      await api.post('/banking/quick-link/unlink', body);
      toast.success('Unlinked — row returned to inbox');
      // Drop from recent + re-add to queue (front)
      setRecentLinks((prev) => prev.filter((e) => e.id !== entry.id));
      setQueue((prev) => [entry.row, ...prev]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unlink failed');
    }
  };

  const fmt = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // iter387 — keyword extractor. Take the first "word" of the payee
  // label longer than 3 chars so we build a match_value that's
  // specific enough to match similar rows but short enough to be
  // stable across minor bank-statement variations.
  const _extractKeyword = (s) => {
    const txt = (s || '').toString().trim();
    if (!txt) return '';
    const parts = txt.replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
    // Prefer the first token ≥4 chars, otherwise fall back to the raw first token.
    return (parts.find((p) => p.length >= 4) || parts[0] || '').toUpperCase().slice(0, 40);
  };

  // iter387 — after 2 similar links in the same session, offer to
  // create a banking rule so future imports auto-link. The toast
  // uses sonner's `action` slot so the user can accept/decline
  // without leaving the inbox flow.
  const maybeOfferRule = async ({ selectedRow, purpose, entity }) => {
    const keyword = _extractKeyword(selectedRow.payee_label || selectedRow.party_name || selectedRow.description);
    if (!keyword) return;
    const key = [
      direction,
      keyword,
      purpose.mapTo || purpose.key,
      purpose.key,
      entity.id || '',
    ].join('|');

    const prev = similarLinksRef.current.get(key) || { count: 0 };
    const next = { count: prev.count + 1, keyword, purpose, entity };
    similarLinksRef.current.set(key, next);

    // Only offer once per key per session; require ≥2 hits.
    if (next.count < 2 || toastedKeysRef.current.has(key)) return;
    toastedKeysRef.current.add(key);

    const createRule = async () => {
      try {
        // Shape the rule body based on direction + purpose. For
        // incoming we use the `incoming_other` kind so the engine
        // doesn't try to create a supplier payment.
        const entity_type = purpose.mapTo || purpose.key;
        let kind = 'expense';
        if (direction === 'incoming') kind = 'incoming_other';
        else if (entity_type === 'supplier') kind = 'supplier_payment';

        const body = {
          name: `Auto-link ${keyword} → ${purpose.label}`,
          match_type: 'contains',
          match_value: keyword,
          direction,
          kind,
          category: purpose.label,
          vendor_name: entity.name || purpose.label,
          supplier_id: entity_type === 'supplier' ? entity.id : null,
          payee_id: entity_type !== 'supplier' ? entity.id : null,
          priority: 120, // higher than system defaults so it fires first
          enabled: true,
        };
        await api.post('/banking/rules', body);
        toast.success(`Rule created — future "${keyword}" ${direction} lines will auto-link to ${purpose.label}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not create rule');
      }
    };

    toast(`${keyword} seen ${next.count}×`, {
      description: `Want future ${direction} lines containing "${keyword}" to auto-link as ${purpose.label}?`,
      duration: 8000,
      action: {
        label: 'Create rule',
        onClick: createRule,
      },
      cancel: { label: 'Not now' },
    });
  };

  const filteredOptions = pickerSearch
    ? pickerOptions.filter((o) => (o.name + ' ' + (o.sub || '')).toLowerCase().includes(pickerSearch.toLowerCase()))
    : pickerOptions;

  // Top-confidence matches ready for one-click bulk accept. We surface
  // a single CTA that accepts everything ≥90% in one API call.
  const topMatchCount = queue.filter(
    (r) => (r.suggested_match?.score || 0) >= 90,
  ).length;

  // iter412 — Stale-only filter (?stale=1). A row is "stale" when its
  // txn date is ≥7 days old. We stamp `_is_stale` + `_age_days` on
  // every row so the badge can render even when the filter is OFF;
  // the filter just narrows the list.
  const visibleQueue = useMemo(() => {
    const now = Date.now();
    const decorated = queue.map((r) => {
      const ts = (r.date || '').slice(0, 10);
      let age = 0;
      if (ts) {
        const d = new Date(ts).getTime();
        if (!Number.isNaN(d)) age = Math.floor((now - d) / 86400000);
      }
      return { ...r, _age_days: age, _is_stale: age >= 7 };
    });
    return staleOnly ? decorated.filter((r) => r._is_stale) : decorated;
  }, [queue, staleOnly]);
  const staleCount = useMemo(
    () => visibleQueue.filter((r) => r._is_stale).length,
    [visibleQueue],
  );

  const bulkAcceptTopMatches = async () => {
    if (topMatchCount === 0) return;
    if (!window.confirm(`Accept all ${topMatchCount} top-confidence matches (\u226590%)? This links each row to its suggested entity in one shot.`)) return;
    setSubmitting(true);
    try {
      const targets = queue
        .filter((r) => (r.suggested_match?.score || 0) >= 90)
        .map((r) => ({ id: r.id }));
      const { data } = await api.post('/banking/quick-link/bulk-accept', { rows: targets });
      toast.success(`Accepted ${data.accepted} top match${data.accepted === 1 ? '' : 'es'}`);
      if ((data.errors || []).length > 0) {
        toast.error(`${data.errors.length} rows failed \u2014 see console for details`);
        // eslint-disable-next-line no-console
        console.error('bulk-accept errors', data.errors);
      }
      setProcessedCount((n) => n + (data.accepted || 0));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk accept failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="banking-quick-link-page">
        <PageInfoCallout
          storageKey="banking-quick-link"
          title="What is Quick-link mode?"
          tone="violet"
        >
          <p>
            <strong>Match the leftovers.</strong> After each bank statement import, some lines auto-match and some
            don&apos;t. The leftovers land here. Pick a row on the left, tap the matching entity on the right
            (Supplier · Customer · Employee · Partner), pick the specific person — the downstream record is created
            and the row drops out of the queue.
          </p>
          <p className="mt-2 text-xs opacity-80">
            👉 <strong>Pro tip:</strong> matching the same person twice? Go to <strong>Auto-Rules</strong> and
            teach the system — next time it matches automatically.
          </p>
        </PageInfoCallout>
        <BankingFlowRecap active="/banking-quick-link" />

        {/* iter400 — Detected new platform IBAN banner. Same component
            as on the Banking Hub; renders only when there are
            unmatched IBANs whose description references a platform. */}
        <PlatformIbanBanner days={90} testidPrefix="qlink-plat-iban" />

        <GettingStartedChecklist
          storageKey="banking-quick-link-v1"
          title="Quick-link in 4 steps"
          steps={[
            { key: 'seed', label: 'Seed 3 demo rows to practice the flow', onClick: seedDemo, cta: 'Seed demo' },
            { key: 'tour', label: 'Watch the 30-second guided tour', onClick: () => setShowTour(true), cta: 'Start tour' },
            { key: 'process', label: 'Link 1 outgoing payment to a supplier', href: '#', cta: 'I will do this' },
            { key: 'cleanup', label: 'Clear demo rows when you\'re done practicing', onClick: clearDemo, cta: 'Clear demo' },
          ]}
        />

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold font-outfit flex items-center gap-2">
              <Inbox size={22} className="text-violet-600" /> Banking Quick-link
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {queue.length} unlinked · {processedCount} processed this session
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* 30-second guided walkthrough — seeds demo data on start if
                the inbox is empty, then steps through pick/link/zero-out. */}
            <Button
              onClick={() => setShowTour(true)}
              variant="outline"
              size="sm"
              data-testid="qlink-start-tour"
              className="text-violet-700 border-violet-300 hover:bg-violet-50"
              title="30-second guided walkthrough"
            >
              <PlayCircle size={13} className="mr-1.5" /> Try the workflow
            </Button>
            {/* Bulk-accept high-confidence matches — appears only when the
                server has surfaced ≥1 row at 90%+ confidence. Single
                click processes every one of them via the server-side
                re-scored /banking/quick-link/bulk-accept endpoint. */}
            {topMatchCount > 0 && (
              <Button
                onClick={bulkAcceptTopMatches}
                disabled={submitting}
                size="sm"
                data-testid="qlink-bulk-accept"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                title="Link every row with ≥90% confidence match in one click"
              >
                <CheckCircle2 size={13} className="mr-1.5" />
                Accept all {topMatchCount} top match{topMatchCount === 1 ? '' : 'es'}
              </Button>
            )}
            {hasDemoRows && (
              <Button
                onClick={clearDemo}
                variant="outline"
                size="sm"
                disabled={seeding}
                data-testid="qlink-clear-demo"
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
              >
                <Trash2 size={13} className="mr-1.5" /> Clear demo
              </Button>
            )}
            <Button
              onClick={seedDemo}
              variant="outline"
              size="sm"
              disabled={seeding}
              data-testid="qlink-seed-demo"
              className="text-violet-700 border-violet-200 hover:bg-violet-50"
              title="Insert 3 mock outgoing bank payments so you can try the inbox workflow"
            >
              <Sparkles size={13} className="mr-1.5" /> {seeding ? 'Seeding…' : 'Seed demo queue'}
            </Button>
            <Button onClick={load} variant="outline" size="sm" data-testid="qlink-refresh">
              <RefreshCw size={13} className="mr-1.5" /> Refresh queue
            </Button>
            <Button
              onClick={async () => {
                if (!window.confirm('Re-run smart-match on every bank statement in this tenant?\n\nThis updates only auto-matches — your manual links stay untouched.')) return;
                try {
                  setRematching(true);
                  const r = await api.post('/bank-statements/rematch-all');
                  const s = r.data || {};
                  toast.success(
                    `Re-matched ${s.statements_processed || 0} statement(s) — ` +
                    `${(s.matched_iban || 0) + (s.matched_account || 0) + (s.matched_name_exact || 0) + (s.matched_name_partial || 0)} new links, ` +
                    `${s.manual_links_preserved || 0} manual links preserved.`,
                  );
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Re-match failed');
                } finally {
                  setRematching(false);
                }
              }}
              variant="outline"
              size="sm"
              disabled={rematching}
              data-testid="qlink-rematch-all"
              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              title="Re-run the smart-matcher across every bank statement so newly added beneficiaries / suppliers / employees are picked up."
            >
              <Sparkles size={13} className="mr-1.5" />
              {rematching ? 'Re-matching…' : 'Re-match all statements'}
            </Button>
          </div>
        </div>

        {/* Direction toggle (iter386) — Outgoing ⇄ Incoming switches the
            queue between money-out and money-in flows. Different chips
            are shown in the action pane for each direction. */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="direction-toggle-bar">
          <span className="text-xs text-muted-foreground font-medium">Flow:</span>
          {[
            { key: 'outgoing', label: 'Outgoing (money out)', icon: ArrowUpCircle, tone: 'rose' },
            { key: 'incoming', label: 'Incoming (money in)', icon: ArrowDownCircle, tone: 'emerald' },
          ].map((d) => {
            const Icon = d.icon;
            const active = direction === d.key;
            const activeCls = d.tone === 'emerald'
              ? 'bg-emerald-600 text-white border-emerald-600'
              : 'bg-rose-600 text-white border-rose-600';
            return (
              <button
                key={d.key}
                type="button"
                onClick={() => setDirection(d.key)}
                className={`text-xs rounded-full px-3 py-1 border transition-all inline-flex items-center gap-1.5 ${
                  active ? activeCls : 'bg-white text-muted-foreground border-border hover:border-violet-300 hover:bg-violet-50'
                }`}
                data-testid={`direction-chip-${d.key}`}
              >
                <Icon size={12} /> {d.label}
              </button>
            );
          })}
        </div>

        {/* Confidence filter chips (iter374). 0 = show everything;
            70/85/95 hide below threshold so users can sweep the high-
            confidence batch first. Backed by `min_confidence` server
            param so we don't transfer rows we'll just hide. */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="confidence-filter-bar">
          <span className="text-xs text-muted-foreground font-medium">Confidence:</span>
          {[
            { v: 0, label: 'All', testid: 'all' },
            { v: 70, label: '≥ 70', testid: '70' },
            { v: 85, label: '≥ 85', testid: '85' },
            { v: 95, label: '≥ 95 (auto-pilot)', testid: '95' },
          ].map(opt => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setMinConfidence(opt.v)}
              className={`text-xs rounded-full px-3 py-1 border transition-all ${
                minConfidence === opt.v
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white text-muted-foreground border-border hover:border-violet-300 hover:bg-violet-50'
              }`}
              data-testid={`confidence-chip-${opt.testid}`}
            >
              {opt.label}
            </button>
          ))}
          {minConfidence > 0 && (
            <span className="text-[11px] text-violet-700 ml-1" data-testid="confidence-filter-summary">
              showing rows with suggested match ≥ {minConfidence}%
            </span>
          )}
          <div className="ml-auto flex items-center gap-1.5" data-testid="quicklink-date-filter">
            <span className="text-xs text-muted-foreground font-medium">From</span>
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="text-xs h-7 rounded-md border border-border px-2 bg-white"
              data-testid="quicklink-date-start"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className="text-xs h-7 rounded-md border border-border px-2 bg-white"
              data-testid="quicklink-date-end"
            />
            {(dateStart || dateEnd) && (
              <button
                type="button"
                onClick={() => { setDateStart(''); setDateEnd(''); }}
                className="text-[11px] text-rose-600 hover:underline ml-1"
                data-testid="quicklink-date-clear"
              >
                clear
              </button>
            )}
            {/* iter395 — Toggle to include already-linked rows. Off by
                default so the inbox stays focused on the to-do pile. */}
            <label className="ml-3 inline-flex items-center gap-1.5 text-[11px] cursor-pointer select-none" data-testid="quicklink-include-linked-toggle">
              <input
                type="checkbox"
                checked={includeLinked}
                onChange={(e) => setIncludeLinked(e.target.checked)}
                className="rounded border-stone-300"
              />
              <span className="text-stone-700">Show linked rows</span>
            </label>
            {/* iter412 — Stale-only chip. Mirrors `?stale=1` so the
                SmartReconcile widget link round-trips with this UI. */}
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                if (staleOnly) next.delete('stale');
                else next.set('stale', '1');
                setSearchParams(next, { replace: true });
              }}
              className={`ml-2 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-semibold transition-colors ${
                staleOnly
                  ? 'bg-rose-600 text-white border border-rose-700'
                  : 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100'
              }`}
              data-testid="quicklink-stale-toggle"
              title="Show only rows that have been unassigned for ≥7 days"
            >
              ⚑ Stale only{staleOnly ? ` (${staleCount})` : ''}
            </button>
          </div>
        </div>

        {/* Two-column layout: inbox left, action panel right */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Left: Inbox of unlinked rows */}
          <Card className="lg:col-span-2 max-h-[70dvh] overflow-y-auto" data-testid="qlink-inbox">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-xs text-muted-foreground">Loading queue…</div>
              ) : queue.length === 0 ? (
                <div className="p-8 text-center" data-testid="qlink-empty">
                  <CheckCircle2 size={28} className="text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold">Inbox zero!</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Every {direction} bank payment is linked. Nice work.
                  </p>
                  <div className="mt-4 pt-4 border-t border-dashed">
                    <p className="text-[11px] text-muted-foreground mb-2">
                      Want to try the workflow?
                    </p>
                    <Button
                      onClick={seedDemo}
                      size="sm"
                      disabled={seeding}
                      data-testid="qlink-empty-seed-demo"
                      className="bg-violet-600 hover:bg-violet-700"
                    >
                      <Sparkles size={13} className="mr-1.5" />
                      {seeding ? 'Seeding…' : 'Seed 3 demo rows'}
                    </Button>
                  </div>
                </div>
              ) : (
                visibleQueue.map((r) => (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => setSelected(r)}
                    data-testid={`qlink-row-${r.id}`}
                    className={`w-full text-left px-3 py-2.5 border-b last:border-b-0 hover:bg-muted/50 transition-colors ${
                      selected?.id === r.id ? 'bg-violet-50 dark:bg-violet-950/20 border-l-4 border-l-violet-600' : ''
                    }`}
                  >
                    {r._is_stale && (
                      <div className="text-[9px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 font-semibold mb-1" data-testid={`qlink-stale-${r.id}`}>
                        ⚑ {r._age_days}d unassigned
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold truncate flex-1" title={r.payee_label || r.party_name || r.description || '—'}>
                        {r.payee_label || r.party_name || r.description || '—'}
                      </span>
                      <span className="text-sm tabular-nums font-semibold text-rose-700">
                        {fmt(r.amount)}
                      </span>
                    </div>
                    {/* iter409 — When the smart-matcher has already
                        resolved a beneficiary at upload time, surface
                        the raw bank label as a tiny subline so the user
                        sees BOTH "Ahmed Abdullah" (resolved) and the
                        original "AHMED ABDULLAH SARIE TRANSFER" (raw).
                        Helps users trust the auto-resolve. */}
                    {r.resolved_link_name && r.raw_party_name
                      && r.raw_party_name.toUpperCase() !== (r.resolved_link_name || '').toUpperCase() && (
                      <div className="text-[10px] text-muted-foreground/70 truncate mt-0.5"
                           title={r.raw_party_name}>
                        from bank: {r.raw_party_name}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                      <span>{(r.date || '').slice(0, 10)}</span>
                      {r.reference ? <span className="opacity-60">· ref {r.reference.slice(0, 18)}</span> : null}
                      {/* iter409 — Auto-resolved badge. Shows when the
                          upload-time matcher already linked this txn to
                          a real entity. Distinct from `suggested_match`
                          which is a real-time scoring hint — this one
                          means "we already know who this is". */}
                      {r.resolved_link_type && r.resolved_link_name && !r.suggested_match && (
                        <span
                          className="ml-auto inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-semibold max-w-[180px] truncate"
                          title={`Auto-resolved by ${r.resolved_confidence || 'matcher'}: ${r.resolved_link_type} — ${r.resolved_link_name}`}
                          data-testid={`qlink-resolved-${r.id}`}
                        >
                          <CheckCircle2 size={9} className="shrink-0" />
                          <span className="opacity-70 uppercase tracking-wider">{r.resolved_link_type?.[0] || '?'}</span>
                          <span className="truncate">{r.resolved_link_name}</span>
                        </span>
                      )}
                      {/* High-confidence match chip — shown only when
                          the backend found a ≥70-pt match so the user
                          can spot "easy wins" from the list.
                          iter395h — Now shows the match NAME inline, not
                          just the score, so users immediately recognise
                          which person/supplier this row maps to. */}
                      {r.suggested_match && (
                        <span
                          className="ml-auto inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold max-w-[180px] truncate"
                          title={`Suggested: ${r.suggested_match.link_type} — ${r.suggested_match.entity_name} (${r.suggested_match.score}% · ${r.suggested_match.reason})`}
                        >
                          <span className="opacity-70 uppercase tracking-wider">{r.suggested_match.link_type?.[0] || '?'}</span>
                          <span className="truncate">{r.suggested_match.entity_name}</span>
                          <span className="opacity-60">({r.suggested_match.score}%)</span>
                        </span>
                      )}
                      {/* Tiny type badge so users see whether a row is
                          a bank-payment vs an auto-link "Unassigned"
                          statement transaction — they process the same
                          way but have different audit trails. */}
                      {r.row_type === 'statement_txn' && (
                        <span
                          className={`${r.suggested_match ? '' : 'ml-auto'} text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase tracking-wide`}
                          title="Unassigned bank-statement transaction. Linking will also create a beneficiary so future statements match automatically."
                        >
                          Stmt
                        </span>
                      )}
                      {/* iter395 — When `Show linked rows` is on, surface
                          a chip so the user instantly spots which rows
                          have already been linked (they're read-only). */}
                      {r.is_linked && (
                        <span
                          className={`${r.suggested_match || r.row_type === 'statement_txn' ? '' : 'ml-auto'} text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-wide`}
                          title="This row is already linked to a downstream record."
                        >
                          Linked
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          {/* Right: Action panel for the selected row */}
          <Card className="lg:col-span-3" data-testid="qlink-action-panel">
            <CardContent className="p-4">
              {!selected ? (
                <div className="text-center py-12 text-sm text-muted-foreground">
                  Pick a row from the inbox to start linking.
                </div>
              ) : (
                <>
                  <div className="rounded-xl bg-muted/40 p-3 mb-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <div className="text-base font-bold">
                          {selected.payee_label || selected.party_name || selected.description || '—'}
                          {selected.row_type === 'statement_txn' && (
                            <span
                              className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase tracking-wide"
                              title="From a bank statement. Linking auto-creates a beneficiary so future statements tag this counterparty automatically."
                            >
                              Statement
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {(selected.date || '').slice(0, 10)} · {selected.bank_account_name || selected.bank_name || 'No bank'} · {selected.reference || 'No ref'}
                          {selected.file_name ? ` · ${selected.file_name}` : ''}
                        </div>
                      </div>
                      <div className="text-2xl font-bold tabular-nums text-rose-700">{fmt(selected.amount)}</div>
                    </div>
                    {selected.description && selected.description !== selected.payee_label ? (
                      <div className="text-xs text-muted-foreground mt-2 italic">{selected.description}</div>
                    ) : null}
                  </div>

                  {/* Smart-rank "One-click accept" — when the server has a
                      high-confidence match for the selected row, offer a
                      single button that links to the suggested entity
                      without opening the picker. Gives the user the "breeze
                      through the top 20" experience they asked for. */}
                  {selected.suggested_match && (
                    <div
                      className="rounded-xl border-2 border-emerald-300 bg-emerald-50 px-4 py-3 flex items-center gap-3"
                      data-testid="qlink-smart-suggest"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold text-emerald-800 uppercase tracking-wide">
                          Best match — {selected.suggested_match.score}% confidence · {selected.suggested_match.reason}
                        </div>
                        {/* iter395h — Surface the matched name and its
                            entity type prominently so the user can see
                            exactly WHICH person/supplier/customer in
                            their data is being matched (was previously
                            tucked into a single line like
                            "customer · Saud" — easy to miss). */}
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className="text-base font-bold text-emerald-950 truncate">
                            {selected.suggested_match.entity_name || '—'}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded font-semibold shrink-0">
                            {selected.suggested_match.link_type === 'beneficiary' ? 'beneficiary'
                              : selected.suggested_match.link_type === 'supplier' ? 'supplier'
                              : selected.suggested_match.link_type === 'customer' ? 'customer'
                              : selected.suggested_match.link_type === 'employee' ? 'employee'
                              : selected.suggested_match.link_type === 'partner' ? 'partner'
                              : selected.suggested_match.link_type}
                          </span>
                        </div>
                        <div className="text-[10px] text-emerald-700/80 mt-0.5 truncate">
                          Bank line: <span className="font-medium">{selected.description || selected.payee_label || '-'}</span>
                        </div>
                      </div>
                      <Button
                        onClick={() => submitLink({
                          id: selected.suggested_match.link_id,
                          name: selected.suggested_match.entity_name,
                        }, { key: selected.suggested_match.link_type, label: selected.suggested_match.link_type, mapTo: selected.suggested_match.link_type })}
                        disabled={submitting}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        size="sm"
                        data-testid="qlink-smart-accept"
                      >
                        <CheckCircle2 size={14} className="mr-1.5" />
                        {submitting ? 'Linking…' : 'Accept'}
                      </Button>
                    </div>
                  )}

                  {/* iter388 — Duplicate-candidate panel. Shows when the
                      backend thinks this bank row is the same event as one
                      the user already entered manually. One-click links to
                      the existing doc, or dismiss to fall through to the
                      normal chip grid below. */}
                  {(dupLoading || dupCandidates.length > 0) && (
                    <div
                      className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3"
                      data-testid="dup-candidates-panel"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Copy size={14} className="text-amber-700" />
                          <div className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                            Already entered?
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={dismissDupPanel}
                          className="text-[11px] text-amber-800 hover:underline"
                          data-testid="dup-dismiss"
                        >
                          Not a match
                        </button>
                      </div>
                      {dupLoading && dupCandidates.length === 0 ? (
                        <div className="text-xs text-amber-800/70">Checking your records…</div>
                      ) : (
                        <>
                          <div className="text-xs text-amber-900 mb-2">
                            Found {dupCandidates.length} existing record{dupCandidates.length === 1 ? '' : 's'} that may already be this transaction. Link to it instead of creating a duplicate?
                          </div>
                          <div className="space-y-1.5">
                            {dupCandidates.map((c) => (
                              <button
                                key={`${c.collection}:${c.id}`}
                                type="button"
                                onClick={() => submitLinkToExisting(c)}
                                disabled={submitting}
                                data-testid={`dup-pick-${c.collection}-${c.id}`}
                                className="w-full text-left rounded-lg border border-amber-200 bg-white px-3 py-2 hover:bg-amber-100 hover:border-amber-400 transition-colors disabled:opacity-50"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-amber-950 truncate">
                                      {c.party_name}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground flex items-center gap-2 mt-0.5">
                                      <span className="uppercase tracking-wide">{c.collection.replace('_', ' ')}</span>
                                      <span>·</span>
                                      <span>{c.date}</span>
                                      {c.category && <><span>·</span><span className="truncate">{c.category}</span></>}
                                    </div>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <div className="text-sm font-bold tabular-nums">{cur()} {fmt(c.amount)}</div>
                                    <div className="text-[10px] text-amber-700 inline-flex items-center gap-0.5">
                                      <Link2 size={9} /> Link to this
                                    </div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                          <div className="text-[10px] text-amber-800/70 mt-2 leading-relaxed">
                            Tip: linking avoids a duplicate in reports. If this isn&apos;t any of the above, click <strong>Not a match</strong> and use the chip grid below to create a new downstream record.
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="text-xs font-semibold mb-2">
                    {selected.suggested_match
                      ? (direction === 'incoming' ? 'Or pick a purpose:' : 'Or link to a different entity:')
                      : (direction === 'incoming' ? 'What is this payment for?' : 'Link to:')}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="qlink-chip-grid">
                    {(direction === 'incoming' ? INCOMING_PURPOSES : ENTITY_TYPES)
                      .filter(t => partnersEnabled || t.key !== 'partner')
                      .map((t) => {
                      const Icon = t.icon;
                      // Each tone uses a static class lookup so PurgeCSS keeps it.
                      const toneCls = {
                        amber: 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100',
                        sky: 'border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100',
                        emerald: 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
                        violet: 'border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100',
                        rose: 'border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100',
                        stone: 'border-stone-300 bg-stone-50 text-stone-800 hover:bg-stone-100',
                      }[t.tone] || 'border-stone-300 bg-stone-50 text-stone-800 hover:bg-stone-100';
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => openPicker(t)}
                          data-testid={`qlink-pick-${t.key}`}
                          className={`rounded-xl border-2 px-3 py-3 text-center transition-colors ${toneCls}`}
                        >
                          <Icon size={20} className="mx-auto mb-1.5" />
                          <div className="text-xs font-semibold">{t.label}</div>
                          {t.help && <div className="text-[9px] opacity-70 mt-0.5 leading-tight">{t.help}</div>}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
                    {direction === 'incoming' ? (
                      <>
                        <strong>Customer Payment</strong> settles an invoice ·
                        <strong> Advance</strong> is pre-paid ·
                        <strong> Refund</strong> reverses an earlier expense ·
                        <strong> Other Income</strong> is miscellaneous revenue.
                      </>
                    ) : (
                      <>
                        <strong>Supplier</strong> creates a supplier payment ·
                        <strong> Customer</strong> creates a refund ·
                        <strong> Employee</strong> creates a salary advance ·
                        <strong> Partner</strong> creates a partner draw ·
                        <strong> Expense Cat.</strong> creates a categorised expense ·
                        <strong> Government</strong> tags as a government fee.
                      </>
                    )}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* iter391 — Recently linked panel. Tracks links created in this
          session so the user can undo a wrong click without leaving
          the page. Survives until page reload. */}
      {recentLinks.length > 0 && (
        <Card className="mt-4" data-testid="qlink-recent-links">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Recently linked this session ({recentLinks.length})
              </div>
              <button
                type="button"
                onClick={() => setRecentLinks([])}
                className="text-[11px] text-muted-foreground hover:text-foreground"
                data-testid="qlink-recent-clear"
              >
                Clear list
              </button>
            </div>
            <div className="text-[11px] text-muted-foreground mb-2">
              Made a wrong link? <strong>Undo</strong> deletes the new record and puts the row back in your inbox. <strong>Detach only</strong> keeps the record but removes the bank link.
            </div>
            <div className="space-y-1.5">
              {recentLinks.map((entry) => (
                <div
                  key={entry.id}
                  className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 flex items-center gap-3"
                  data-testid={`qlink-recent-row-${entry.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {entry.row.payee_label || entry.row.party_name || entry.row.description || '—'}
                      <span className="ml-2 text-[10px] font-normal text-muted-foreground">
                        → {entry.link.purpose_label}
                        {entry.link.subtype && ` · ${entry.link.subtype}`}
                      </span>
                    </div>
                    <div className="text-[10px] text-muted-foreground inline-flex items-center gap-2 mt-0.5">
                      <span>SAR {fmt(entry.row.amount)}</span>
                      <span>·</span>
                      <span>{(entry.row.date || '').slice(0, 10)}</span>
                      {entry.link.name && entry.link.name !== entry.link.purpose_label && (
                        <><span>·</span><span className="truncate">{entry.link.name}</span></>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => undoRecentLink(entry, true)}
                      disabled={!entry.link.id}
                      className="px-2.5 py-1 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-semibold disabled:opacity-40"
                      data-testid={`qlink-undo-${entry.id}`}
                      title={entry.link.id ? 'Delete the linked record and put this row back in the inbox' : 'Cannot undo — this was a tag-only link'}
                    >
                      Undo
                    </button>
                    <button
                      type="button"
                      onClick={() => undoRecentLink(entry, false)}
                      disabled={!entry.link.id}
                      className="px-2.5 py-1 rounded-md bg-stone-200 hover:bg-stone-300 text-stone-800 text-[10px] font-semibold disabled:opacity-40"
                      data-testid={`qlink-detach-${entry.id}`}
                      title={entry.link.id ? 'Just detach the bank link, keep the record' : 'Cannot detach — this was a tag-only link'}
                    >
                      Detach
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entity picker — appears when the user picks a type. Extracted
          to QuickLinkPickerDialog (iter399) to keep this file focused on
          state management + the inbox/recent-links UI. */}
      <QuickLinkPickerDialog
        pickerType={pickerType}
        setPickerType={setPickerType}
        SUBTYPES={SUBTYPES}
        pickerSubtype={pickerSubtype}
        setPickerSubtype={setPickerSubtype}
        branches={branches}
        pickerBranchId={pickerBranchId}
        setPickerBranchId={setPickerBranchId}
        pickerDescription={pickerDescription}
        setPickerDescription={setPickerDescription}
        pickerSearch={pickerSearch}
        setPickerSearch={setPickerSearch}
        pickerLoading={pickerLoading}
        suggestedTop={suggestedTop}
        filteredOptions={filteredOptions}
        submitLink={submitLink}
        submitting={submitting}
      />

      {/* 30-second guided walkthrough — 5 short steps that map 1:1 to the
          on-screen actions. On "Try it now", we auto-seed 3 demo rows so
          the user can exercise the flow without real bank data. */}
      <PageTour
        open={showTour}
        onOpenChange={setShowTour}
        tourName="Quick-link in 30 seconds"
        testidPrefix="qlink-tour"
        finishLabel="Seed demo & try it"
        onFinish={async () => {
          // If they already have rows, don't stack more — just focus the page.
          if (queue.length === 0) {
            await seedDemo();
          } else {
            toast.info('You already have rows in the queue — try linking one to a supplier!');
          }
        }}
        steps={[
          {
            icon: Inbox,
            title: '1. The inbox shows unlinked payments',
            body: (
              <p>
                The left column lists every <strong>outgoing bank payment that hasn&rsquo;t been linked</strong> to
                a supplier, customer, employee, or partner yet. Think of it as your &ldquo;to-do&rdquo; pile —
                you process rows to zero, just like clearing email.
              </p>
            ),
          },
          {
            icon: ArrowRight,
            title: '2. Pick a row',
            body: (
              <p>
                Click any row on the left. Its details (payee, date, amount, bank, reference) load into the
                <strong> action panel on the right</strong>. The first row is auto-selected when the page loads so
                you can dive in immediately.
              </p>
            ),
          },
          {
            icon: Truck,
            title: '3. Pick the entity type',
            body: (
              <p>
                On the right, click one of the 4 big coloured buttons: <strong>Supplier</strong>,
                <strong> Customer</strong>, <strong>Employee</strong>, or <strong>Partner</strong>. Each creates
                the right downstream record for you — a supplier payment, a refund, a salary advance, or a
                partner draw.
              </p>
            ),
          },
          {
            icon: Users,
            title: '4. Pick the specific person',
            body: (
              <p>
                A searchable picker opens. Type the first few letters of the name, click the match, and
                you&rsquo;re done. The system creates the downstream record, links it back to this bank
                payment, and <strong>drops the row out of your inbox</strong>. The next row auto-selects so
                you stay in flow.
              </p>
            ),
          },
          {
            icon: CheckCircle2,
            title: '5. Inbox zero!',
            body: (
              <>
                <p className="mb-2">
                  When the inbox is empty, you&rsquo;ve processed every outgoing bank payment. The counter
                  at the top shows how many you cleared this session.
                </p>
                <p>
                  Ready to try it? Hit <strong>&ldquo;Seed demo &amp; try it&rdquo;</strong> below — we&rsquo;ll
                  drop 3 safe mock rows into the inbox so you can practice the whole flow. Clean them up
                  anytime with <strong>Clear demo</strong>.
                </p>
              </>
            ),
          },
        ]}
      />
    </DashboardLayout>
  );
}
